import {FormGroup} from "@angular/forms";
import {TYPE} from "../../../shared/types/types";
import {Timezone} from "@syncfusion/ej2-angular-schedule";


//Helper method to convert back any selected time to UTC 0 because we will save it like that in the DB
export function recalculateTimezoneData(calendarEventForm: FormGroup): { startUTC: Date; endUTC: Date; } {
  const start = calendarEventForm.get('onDateStart')?.value;
  const end = calendarEventForm.get('onDateEnd')?.value;

  const timezone = new Timezone();
  const startZone = calendarEventForm.get('startTimeZone')?.value; // e.g., 'Europe/Berlin'
  const endZone = calendarEventForm.get('endTimeZone')?.value;
  const startUTC = timezone.convert(start, startZone, 'UTC');
  const endUTC = timezone.convert(end, endZone, 'UTC');
  return {startUTC, endUTC};
}


export function buildRecurrenceString(rule: TYPE.RecurrenceRule): string {
  if (!rule || rule.repeatType === 'never') return '';

  const parts: string[] = [];

  parts.push(`FREQ=${rule.repeatType.toUpperCase()}`);
  parts.push(`INTERVAL=${rule.repeatEvery || 1}`);

  if (rule.repeatEndType === 'count' && rule.repeatDaysCount) {
    parts.push(`COUNT=${rule.repeatDaysCount}`);
  } else if (rule.repeatEndType === 'until' && rule.repeatDaysUntil) {
    const until = new Date(rule.repeatDaysUntil);
    const utc = until.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    parts.push(`UNTIL=${utc}`);
  }

  if (rule.repeatType === 'weekly' && rule.repeatOn?.length) {
    parts.push(`BYDAY=${rule.repeatOn.join(',')}`);
  }

  if (rule.repeatType === 'monthly') {
    if (rule.monthlyRepeatMode === true) {
      parts.push(`BYMONTHDAY=${rule.monthlyRepeatDay}`);
    } else {
      parts.push(`BYSETPOS=${rule.monthlyOrdinal}`);
      parts.push(`BYDAY=${rule.monthlyWeekday}`);
    }
  }

  if (rule.repeatType === 'yearly') {
    parts.push(`BYMONTH=${rule.repeatMonth}`);
    if (rule.monthlyRepeatMode === true) {
      parts.push(`BYMONTHDAY=${rule.monthlyRepeatDay}`);
    } else {
      parts.push(`BYSETPOS=${rule.monthlyOrdinal}`);
      parts.push(`BYDAY=${rule.monthlyWeekday}`);
    }
  }

  return parts.join(';');
}

//Validation method related to edge cases in the form
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function validateRecurrencePattern(startUTC: Date, endUTC: Date, formValue: any): string | null {
  const durationMs = endUTC.getTime() - startUTC.getTime();
  const durationMinutes = durationMs / 60000;
  if (formValue.repeatType !== 'never') {
    // 1. End date before start date in recurrence UNTIL
    if (formValue.repeatEndType === 'until' && formValue.repeatDaysUntil) {
      const untilDate = new Date(formValue.repeatDaysUntil);
      if (untilDate.getTime() < startUTC.getTime()) {
        return 'The selected "Repeat Until" date is before the event start date.';
      }
    }
    // 2. Duration longer than frequency (simplified rule)
    if (formValue.repeatType === 'daily' && durationMinutes >= 1440) {
      return 'The duration of the event must be shorter than how frequently it occurs. Shorten the duration, or change the recurrence pattern.';
    }
    if (formValue.repeatType === 'weekly' && durationMinutes >= 10080) {
      return 'Weekly recurring events must be shorter than one week.';
    }
    if (formValue.repeatType === 'monthly' && durationMinutes >= 43200) {
      return 'Monthly recurring events must be shorter than one month.';
    }
    if (formValue.repeatType === 'yearly' && durationMinutes >= 525600) {
      return 'Yearly recurring events must be shorter than one year.';
    }
    // 3. Monthly repeat on days that may not exist in all months (e.g. 31st)
    if ((formValue.repeatType === 'monthly' || formValue.repeatType === 'yearly') &&
      formValue.monthlyRepeatMode === true && formValue.monthlyRepeatDay > 28) {
      return 'Some months have fewer than the selected date. For these months, the occurrence will fall on the last date of the month.';
    }
    // 4. Prevent same-day duplicate occurrences - We need to install version 29, there you can either allow it or not
  }
  return null;
}